import type {
  EnumSmartFieldDataTypeApi,
  EnumSmartFieldUiComponentTypeApi,
  SmartFieldUiComponentApi,
} from '@ingenico/api-sdk-configuration-public';

import { enumSmartFieldDataType, enumSmartFieldUiComponentType } from '@ingenico/api-sdk-configuration-public';
import type { SmartFieldUiComponentSelectChoiceApi } from '@ingenico/react-smartfield-utils';
import { toString } from 'lodash-es';

export function formatEmptyValue(value?: string): string {
  return value || '-';
}

export function formatBooleanValue(value?: boolean): string {
  return value ? 'Yes' : 'No';
}

export function isLabeledComponent(type?: EnumSmartFieldUiComponentTypeApi): boolean {
  return ['DATE_TIME', 'INPUT', 'MULTI_SELECT', 'SELECT', 'TOGGLE'].includes(toString(type));
}

export function isRequirableComponent(type?: EnumSmartFieldUiComponentTypeApi): boolean {
  return ['DATE_TIME', 'INPUT', 'MULTI_SELECT', 'SELECT'].includes(toString(type));
}

export function getComponentLabel(uiComponent?: SmartFieldUiComponentApi): string {
  if (!uiComponent?.options) {
    return '';
  }

  switch (uiComponent.type) {
    case enumSmartFieldUiComponentType.DATE_TIME: {
      return toString(uiComponent.options.dateTime?.label);
    }
    case enumSmartFieldUiComponentType.INPUT: {
      return toString(uiComponent.options.inputNumber?.label || uiComponent.options.inputString?.label);
    }
    case enumSmartFieldUiComponentType.MULTI_SELECT: {
      return toString(uiComponent.options.multiSelect?.label);
    }
    case enumSmartFieldUiComponentType.SELECT: {
      return toString(uiComponent.options.select?.label);
    }
    case enumSmartFieldUiComponentType.TOGGLE: {
      return toString(uiComponent.options.toggle?.label);
    }
    default: {
      return '';
    }
  }
}

export function getComponentIsRequired(uiComponent?: SmartFieldUiComponentApi): boolean {
  if (!uiComponent?.options) {
    return false;
  }

  switch (uiComponent.type) {
    case enumSmartFieldUiComponentType.DATE_TIME: {
      return Boolean(uiComponent.options.dateTime?.isRequired);
    }
    case enumSmartFieldUiComponentType.INPUT: {
      return Boolean(uiComponent.options.inputNumber?.isRequired || uiComponent.options.inputString?.isRequired);
    }
    case enumSmartFieldUiComponentType.MULTI_SELECT: {
      return Boolean(uiComponent.options.multiSelect?.isRequired);
    }
    case enumSmartFieldUiComponentType.SELECT: {
      return Boolean(uiComponent.options.select?.isRequired);
    }
    default: {
      return false;
    }
  }
}

export function getSpecificFieldSettings(uiComponent?: SmartFieldUiComponentApi) {
  if (!uiComponent) {
    return {};
  }

  const { type, options } = uiComponent;

  switch (type) {
    case enumSmartFieldUiComponentType.DATE_TIME: {
      return {
        dateTimeSettings: options.dateTime,
      };
    }

    case enumSmartFieldUiComponentType.INPUT: {
      return {
        inputSettings: options.inputNumber || options.inputString,
      };
    }

    case enumSmartFieldUiComponentType.MULTI_SELECT: {
      return {
        multiSelectSettings: {
          ...options.multiSelect,
          defaultSelectedLabels: getSelectedLabels(
            options.multiSelect?.choices,
            options.multiSelect?.defaultSelectedIds,
          ),
          choicesValues: getChoicesValues(options.multiSelect?.choices),
        },
      };
    }

    case enumSmartFieldUiComponentType.SELECT: {
      return {
        selectSettings: {
          ...options.select,
          defaultSelectedLabel: getSelectedLabel(options.select?.choices, options.select?.defaultSelectedId),
          choicesValues: getChoicesValues(options.select?.choices),
        },
      };
    }

    case enumSmartFieldUiComponentType.TABLE: {
      return {
        tableSettings: {
          columnNames: options.table?.columns.map((column) => column.name),
        },
      };
    }

    case enumSmartFieldUiComponentType.TOGGLE: {
      return {
        toggleSettings: options.toggle,
      };
    }

    default: {
      return {};
    }
  }
}

function getSelectedLabels(choices?: SmartFieldUiComponentSelectChoiceApi[], selectedIds?: number[]): string[] {
  if (!choices || !selectedIds) {
    return [];
  }
  return choices.filter((choice) => selectedIds.includes(choice.id)).map((choice) => choice.label);
}

function getChoicesValues(choices?: SmartFieldUiComponentSelectChoiceApi[]): string[] {
  if (!choices) {
    return [];
  }
  return choices.map((choice) => choice.value);
}

function getSelectedLabel(choices?: SmartFieldUiComponentSelectChoiceApi[], selectedId?: number): string {
  if (!choices || !selectedId) {
    return '';
  }
  return choices.find((choice) => choice.id === selectedId)?.label || '';
}

export function getSmartFieldDataType(dataType?: EnumSmartFieldDataTypeApi): string {
  switch (dataType) {
    case enumSmartFieldDataType.BOOLEAN: {
      return 'boolean';
    }
    case enumSmartFieldDataType.DATE: {
      return 'date';
    }
    case enumSmartFieldDataType.ENUM: {
      return 'enum';
    }
    case enumSmartFieldDataType.NUMBER: {
      return 'number';
    }
    case enumSmartFieldDataType.STRING: {
      return 'string';
    }
    case enumSmartFieldDataType.STRUCTURE: {
      return 'structure';
    }
    default: {
      return '-';
    }
  }
}
